import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import {
  X, MapPin, Ruler, ChevronRight, Phone, Mail, User, Building2,
  MessageSquare, Send, Filter, ZoomIn, ZoomOut, Maximize2, RotateCcw,
  CheckCircle, Star, Compass, ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  available: { fill: "#22c55e", stroke: "#16a34a", label: "Available" },
  reserved: { fill: "#facc15", stroke: "#ca8a04", label: "Reserved" },
  sold: { fill: "#ef4444", stroke: "#dc2626", label: "Sold" },
  premium: { fill: "#3b82f6", stroke: "#2563eb", label: "Premium" },
};

function formatINR(n) {
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)} Lakh`;
  return n.toLocaleString("en-IN");
}

/* ── SVG Map Layout Engine ── */
function getPlotRects(plots) {
  const rects = {};
  const VB_W = 1400, VB_H = 860;
  const PAD = 20;
  const GAP = 3;

  // Row definitions (y positions)
  const rows = {
    river:    { y: 0, h: 30 },
    green:    { y: 30, h: 25 },
    top:      { y: 60, h: 95 },   // P-31..P-42 + R-3,R-2
    road15:   { y: 158, h: 28 },
    midUpper: { y: 189, h: 90 },  // P-23..P-30 + R-1
    midLower: { y: 282, h: 75 },  // P-17..P-22
    road12:   { y: 360, h: 24 },
    bottom:   { y: 387, h: 75 },  // P-5..P-16
    south:    { y: 465, h: 30 },
  };

  // Group plots by row
  const byRow = {};
  plots.forEach(p => {
    if (!byRow[p.row]) byRow[p.row] = [];
    byRow[p.row].push(p);
  });
  Object.values(byRow).forEach(arr => arr.sort((a, b) => a.order - b.order));

  // Layout helper: distribute plots in a horizontal band
  const layoutRow = (rowPlots, startX, endX, yTop, height) => {
    if (!rowPlots || rowPlots.length === 0) return;
    const totalSize = rowPlots.reduce((s, p) => s + p.plot_size_sqmt, 0);
    const availW = endX - startX - (rowPlots.length - 1) * GAP;
    let cx = startX;
    rowPlots.forEach(p => {
      const w = Math.max(40, (p.plot_size_sqmt / totalSize) * availW);
      rects[p.plot_id] = { x: cx, y: yTop, w, h: height };
      cx += w + GAP;
    });
  };

  // West column: R-3, R-2, R-1 (stacked vertically)
  const west = byRow["west"] || [];
  if (west.length) {
    const wColW = 90;
    const totalH = rows.top.h + rows.road15.h + rows.midUpper.h;
    const totalSize = west.reduce((s, p) => s + p.plot_size_sqmt, 0);
    let cy = rows.top.y;
    west.forEach(p => {
      const h = (p.plot_size_sqmt / totalSize) * totalH;
      rects[p.plot_id] = { x: PAD, y: cy, w: wColW, h: h - GAP };
      cy += h;
    });
  }

  // Top row: P-31..P-42 (after west column)
  const westW = west.length ? 90 + GAP + PAD : PAD;
  layoutRow(byRow["top"], westW, VB_W - PAD, rows.top.y, rows.top.h);

  // Mid-upper: P-30..P-23
  layoutRow(byRow["mid-upper"], westW, VB_W - 160, rows.midUpper.y, rows.midUpper.h);

  // East column: P-1..P-4 (stacked on right side)
  const east = byRow["east"] || [];
  if (east.length) {
    const eColX = VB_W - 145;
    const eColW = 125;
    const totalH = rows.midUpper.h + rows.midLower.h + rows.road12.h + rows.bottom.h;
    const totalSize = east.reduce((s, p) => s + p.plot_size_sqmt, 0);
    let cy = rows.midUpper.y;
    east.forEach(p => {
      const h = Math.max(40, (p.plot_size_sqmt / totalSize) * totalH);
      rects[p.plot_id] = { x: eColX, y: cy, w: eColW, h: h - GAP };
      cy += h;
    });
  }

  // Mid-lower: P-22..P-17
  const eastStartX = east.length ? VB_W - 160 : VB_W - PAD;
  layoutRow(byRow["mid-lower"], westW + 50, eastStartX, rows.midLower.y, rows.midLower.h);

  // Bottom: P-16..P-5
  layoutRow(byRow["bottom"], westW, eastStartX, rows.bottom.y, rows.bottom.h);

  return { rects, rows, VB_W, VB_H };
}


/* ── Reservation Form ── */
function ReservationForm({ plot, onClose, onSuccess }) {
  const [form, setForm] = useState({ customer_name: "", mobile: "", email: "", company_name: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/site-reservations`, { ...form, plot_id: plot.plot_id });
      toast.success(`Plot ${plot.plot_id} reserved successfully!`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reservation failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      data-testid="reservation-modal"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }}
        className="relative bg-[#1E293B] border border-white/10 w-full max-w-md z-10 shadow-2xl"
      >
        <div className="bg-slate-900 px-6 py-4 flex items-center justify-between border-b border-white/10">
          <div>
            <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: "uppercase" }}>
              Reserve Plot {plot.plot_id}
            </h3>
            <p className="text-slate-400 text-xs">{plot.plot_size_sqmt.toLocaleString()} sq.mtr &middot; {plot.road_width} road</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1"><User className="inline w-3 h-3 mr-1" />Full Name *</label>
              <input name="customer_name" value={form.customer_name} onChange={handleChange} required
                className="w-full border border-slate-600 px-3 py-2 text-sm bg-slate-800 text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none"
                placeholder="Your name" data-testid="res-name-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1"><Phone className="inline w-3 h-3 mr-1" />Mobile *</label>
              <input name="mobile" value={form.mobile} onChange={handleChange} required
                className="w-full border border-slate-600 px-3 py-2 text-sm bg-slate-800 text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none"
                placeholder="+91 XXXXX XXXXX" data-testid="res-mobile-input" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1"><Mail className="inline w-3 h-3 mr-1" />Email *</label>
            <input name="email" type="email" value={form.email} onChange={handleChange} required
              className="w-full border border-slate-600 px-3 py-2 text-sm bg-slate-800 text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none"
              placeholder="your@email.com" data-testid="res-email-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1"><Building2 className="inline w-3 h-3 mr-1" />Company</label>
            <input name="company_name" value={form.company_name} onChange={handleChange}
              className="w-full border border-slate-600 px-3 py-2 text-sm bg-slate-800 text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none"
              placeholder="Company name (optional)" data-testid="res-company-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1"><MessageSquare className="inline w-3 h-3 mr-1" />Message</label>
            <textarea name="message" value={form.message} onChange={handleChange} rows={2}
              className="w-full border border-slate-600 px-3 py-2 text-sm bg-slate-800 text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none resize-none"
              placeholder="Requirements or questions..." data-testid="res-message-input" />
          </div>
          <div className="bg-orange-500/10 border border-orange-500/30 p-3 text-xs text-orange-300">
            <strong>Payment Policy:</strong> 10% on Booking, 15% on Allotment (30 days), 75% on Lease Deed (6 months)
          </div>
          <button type="submit" disabled={submitting}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 transition-all hover:shadow-[0_0_20px_rgba(249,115,22,0.3)] disabled:opacity-60"
            data-testid="res-submit-btn">
            <Send className="w-4 h-4" /> {submitting ? "Reserving..." : "Reserve This Plot"}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}


/* ── Plot Detail Side Panel ── */
function PlotDetailPanel({ plot, onClose, onReserve }) {
  if (!plot) return null;
  const sc = plot.corner_plot ? STATUS_COLORS.premium : STATUS_COLORS[plot.status] || STATUS_COLORS.available;

  return (
    <motion.div
      initial={{ x: 400, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 400, opacity: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className="absolute right-0 top-0 bottom-0 w-80 bg-[#1E293B] border-l border-white/10 z-20 overflow-y-auto shadow-2xl flex flex-col"
      data-testid="plot-detail-panel"
    >
      {/* Header */}
      <div className="p-5 border-b border-white/10" style={{ backgroundColor: sc.fill + "15" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: sc.fill }} />
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: sc.fill }}>{sc.label}</span>
            {plot.corner_plot && (
              <span className="bg-blue-500/20 text-blue-300 text-xs font-bold px-1.5 py-0.5 flex items-center gap-1">
                <Star className="w-3 h-3" /> Corner
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="panel-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <h2 className="text-white text-3xl font-black" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.02em" }}>
          PLOT {plot.plot_id}
        </h2>
        <p className="text-slate-400 text-xs mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          IIE Sigaddi, SIIDCUL, Kotdwar
        </p>
      </div>

      {/* Data grid */}
      <div className="p-5 flex-1">
        <div className="grid grid-cols-2 gap-3 mb-5">
          {[
            { label: "Size", value: `${plot.plot_size_sqmt.toLocaleString()} sqmt`, icon: Ruler },
            { label: "Type", value: plot.plot_type, icon: Building2 },
            { label: "Road Width", value: plot.road_width, icon: ArrowRight },
            { label: "Facing", value: plot.facing, icon: Compass },
          ].map(item => (
            <div key={item.label} className="bg-slate-800/50 border border-white/[0.06] p-3">
              <div className="flex items-center gap-1 text-slate-500 text-xs mb-1">
                <item.icon className="w-3 h-3" />{item.label}
              </div>
              <div className="text-white text-sm font-semibold">{item.value}</div>
            </div>
          ))}
        </div>

        {/* Price */}
        <div className="bg-slate-800/50 border border-white/[0.06] p-4 mb-5">
          <div className="text-slate-500 text-xs mb-1">Rate per Sq.Mtr</div>
          <div className="text-orange-500 text-2xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {`\u20B9${plot.price_per_sqmt?.toLocaleString("en-IN")}`}
          </div>
          <div className="border-t border-white/[0.06] mt-3 pt-3 flex justify-between items-center">
            <span className="text-slate-500 text-xs">Total Value</span>
            <span className="text-white font-bold text-lg" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {`\u20B9${formatINR(plot.total_price)}`}
            </span>
          </div>
        </div>

        {/* Hectare */}
        <div className="text-xs text-slate-500 mb-5 flex items-center gap-1">
          <MapPin className="w-3 h-3" /> Area: {(plot.plot_size_sqmt / 10000).toFixed(4)} Hectare
        </div>

        {/* Amenities */}
        <div className="mb-5">
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Amenities</div>
          <div className="flex flex-wrap gap-1.5">
            {["Water Supply", "Electricity", "Drainage", "Wide Roads", "Security Gate", "Street Light"].map(a => (
              <span key={a} className="bg-white/5 text-slate-400 text-xs px-2 py-1 border border-white/10 flex items-center gap-1">
                <CheckCircle className="w-3 h-3 text-emerald-400" />{a}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="p-5 border-t border-white/10 space-y-2">
        {plot.status === "available" ? (
          <button
            onClick={() => onReserve(plot)}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 transition-all hover:shadow-[0_0_20px_rgba(249,115,22,0.4)]"
            data-testid="reserve-plot-btn"
          >
            Reserve This Plot <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <div className="w-full text-center py-3 font-bold uppercase tracking-wider text-sm border border-white/10 text-slate-500">
            {plot.status === "reserved" ? "Already Reserved" : "Sold Out"}
          </div>
        )}
        <a
          href={`https://wa.me/919258959058?text=Hi%2C%20I%20am%20interested%20in%20Plot%20${plot.plot_id}%20(${plot.plot_size_sqmt}%20sqmt)`}
          target="_blank" rel="noopener noreferrer"
          className="w-full border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 py-2.5 font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2 transition-colors"
          data-testid="whatsapp-plot-btn"
        >
          WhatsApp Enquiry
        </a>
        <a
          href="tel:+919258959058"
          className="w-full border border-white/10 text-slate-400 hover:text-orange-400 hover:border-orange-500 py-2.5 font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2 transition-colors"
        >
          <Phone className="w-3.5 h-3.5" /> Call Sales: +91-9258959058
        </a>
      </div>
    </motion.div>
  );
}


/* ── Main Interactive Site Map ── */
export default function SiteMapView() {
  const [plots, setPlots] = useState([]);
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [reservingPlot, setReservingPlot] = useState(null);
  const [hoveredPlot, setHoveredPlot] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterCorner, setFilterCorner] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const { t } = useLang();

  const fetchPlots = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/site-plots`);
      setPlots(data);
    } catch { /* fallback to empty */ }
  }, []);

  useEffect(() => { fetchPlots(); }, [fetchPlots]);

  // Scroll-wheel zoom (non-passive so we can prevent page scroll)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const dir = e.deltaY < 0 ? 1 : -1;
      setZoom((z) => Math.max(0.5, Math.min(3, +(z + dir * 0.15).toFixed(2))));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const filteredPlots = useMemo(() => {
    return plots.filter(p => {
      if (filterStatus !== "all" && p.status !== filterStatus) return false;
      if (filterType !== "all" && p.plot_type !== filterType) return false;
      if (filterCorner && !p.corner_plot) return false;
      return true;
    });
  }, [plots, filterStatus, filterType, filterCorner]);

  const layout = useMemo(() => getPlotRects(plots), [plots]);

  const handleZoom = (dir) => setZoom(z => Math.max(0.5, Math.min(3, z + dir * 0.25)));
  const handleReset = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  // Drag-to-pan (enables moving around when the map is enlarged)
  const beginPan = (e) => {
    if (e.button !== 0) return;
    setIsPanning(true);
    panStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };
  const movePan = (e) => {
    if (!isPanning) return;
    setPan({
      x: panStart.current.panX + (e.clientX - panStart.current.x) / zoom,
      y: panStart.current.panY + (e.clientY - panStart.current.y) / zoom,
    });
  };
  const endPan = () => setIsPanning(false);

  const handleReservationSuccess = () => {
    setReservingPlot(null);
    setSelectedPlot(null);
    fetchPlots();
  };

  // Stats
  const stats = useMemo(() => ({
    total: plots.length,
    available: plots.filter(p => p.status === "available").length,
    reserved: plots.filter(p => p.status === "reserved").length,
    sold: plots.filter(p => p.status === "sold").length,
    corner: plots.filter(p => p.corner_plot).length,
  }), [plots]);

  const SITE_PLAN_URL = "https://customer-assets.emergentagent.com/job_21c11252-bbdf-43cd-aaef-576b4f9c6f64/artifacts/8fbiyce9_Screenshot_kotdwarsiteplan.jpeg";

  return (
    <div className="bg-[#0F172A] min-h-screen" data-testid="site-map-view">
      {/* Header Stats */}
      <div className="border-b border-white/10 bg-[#1E293B]">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-5">
            {[
              { color: "#22c55e", label: "Available", count: stats.available },
              { color: "#facc15", label: "Reserved", count: stats.reserved },
              { color: "#ef4444", label: "Sold", count: stats.sold },
              { color: "#3b82f6", label: "Corner/Premium", count: stats.corner },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-2" data-testid={`legend-${s.label.toLowerCase()}`}>
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: s.color }} />
                <span className="text-xs text-slate-400">{s.label}</span>
                <span className="text-xs font-bold text-white" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{s.count}</span>
              </div>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setShowOriginal(!showOriginal)}
              className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 border transition-colors ${showOriginal ? "border-orange-500 text-orange-400 bg-orange-500/10" : "border-slate-600 text-slate-400 hover:border-orange-500"}`}
              data-testid="toggle-original-btn">
              {showOriginal ? "Interactive Map" : "Architect Drawing"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto flex" style={{ minHeight: "calc(100vh - 180px)" }}>
        {/* Filter Panel */}
        <div className="w-56 border-r border-white/10 p-4 flex-shrink-0 hidden lg:block" data-testid="filter-panel">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-orange-500" />
            <span className="text-white text-sm font-bold uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Filters</span>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider font-bold">Status</label>
              {["all", "available", "reserved", "sold"].map(s => (
                <button key={s} onClick={() => setFilterStatus(s)}
                  className={`block w-full text-left px-3 py-1.5 text-xs font-medium mb-1 transition-colors ${filterStatus === s ? "bg-orange-500/10 text-orange-400 border-l-2 border-orange-500" : "text-slate-400 hover:text-white"}`}
                  data-testid={`filter-status-${s}`}>
                  {s === "all" ? "All Plots" : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider font-bold">Type</label>
              {["all", "Industrial", "Commercial", "Residential"].map(tp => (
                <button key={tp} onClick={() => setFilterType(tp)}
                  className={`block w-full text-left px-3 py-1.5 text-xs font-medium mb-1 transition-colors ${filterType === tp ? "bg-orange-500/10 text-orange-400 border-l-2 border-orange-500" : "text-slate-400 hover:text-white"}`}
                  data-testid={`filter-type-${tp}`}>
                  {tp === "all" ? "All Types" : tp}
                </button>
              ))}
            </div>
            <div>
              <button onClick={() => setFilterCorner(!filterCorner)}
                className={`w-full text-left px-3 py-1.5 text-xs font-medium flex items-center gap-2 transition-colors ${filterCorner ? "bg-blue-500/10 text-blue-400 border-l-2 border-blue-500" : "text-slate-400 hover:text-white"}`}
                data-testid="filter-corner-btn">
                <Star className="w-3 h-3" /> Corner Plots Only
              </button>
            </div>
            <div className="border-t border-white/10 pt-4">
              <div className="text-xs text-slate-500 mb-2">Showing</div>
              <div className="text-2xl font-bold text-white" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {filteredPlots.length}
              </div>
              <div className="text-xs text-slate-500">of {plots.length} plots</div>
            </div>
          </div>
        </div>

        {/* Map Area */}
        <div className="flex-1 relative overflow-hidden" ref={containerRef}>
          {/* Zoom Controls */}
          <div className="absolute top-3 left-3 z-10 flex flex-col gap-1">
            <button onClick={() => handleZoom(1)} className="bg-[#1E293B] border border-white/10 p-2 text-white hover:border-orange-500 transition-colors" data-testid="zoom-in-btn">
              <ZoomIn className="w-4 h-4" />
            </button>
            <button onClick={() => handleZoom(-1)} className="bg-[#1E293B] border border-white/10 p-2 text-white hover:border-orange-500 transition-colors" data-testid="zoom-out-btn">
              <ZoomOut className="w-4 h-4" />
            </button>
            <button onClick={handleReset} className="bg-[#1E293B] border border-white/10 p-2 text-white hover:border-orange-500 transition-colors" data-testid="zoom-reset-btn">
              <RotateCcw className="w-4 h-4" />
            </button>
            <button onClick={() => { setZoom(1.5); setPan({ x: 0, y: 0 }); }}
              className="bg-[#1E293B] border border-white/10 p-2 text-white hover:border-orange-500 transition-colors" data-testid="zoom-fit-btn">
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>

          {showOriginal ? (
            <div className="w-full h-full flex items-center justify-center p-8">
              <img src={SITE_PLAN_URL} alt="Original Site Plan" className="max-w-full max-h-full object-contain border border-white/10" data-testid="original-site-plan" />
            </div>
          ) : (
            <div
              onMouseDown={beginPan}
              onMouseMove={movePan}
              onMouseUp={endPan}
              onMouseLeave={endPan}
              style={{
                transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
                transformOrigin: "center center",
                transition: isPanning ? "none" : "transform 0.3s ease",
                cursor: isPanning ? "grabbing" : "grab",
              }}
            >
              <svg
                ref={svgRef}
                viewBox={`0 0 ${layout.VB_W} ${layout.VB_H}`}
                className="w-full"
                style={{ minHeight: "500px" }}
                data-testid="interactive-svg-map"
              >
                {/* Background */}
                <rect x="0" y="0" width={layout.VB_W} height={layout.VB_H} fill="#0F172A" />

                {/* Grid pattern */}
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
                  </pattern>
                </defs>
                <rect x="0" y="0" width={layout.VB_W} height={layout.VB_H} fill="url(#grid)" />

                {/* River */}
                <rect x="0" y={layout.rows.river.y} width={layout.VB_W} height={layout.rows.river.h} fill="#1e3a5f" rx="2" opacity="0.6" />
                <text x={layout.VB_W / 2} y={layout.rows.river.y + 18} textAnchor="middle" fill="#60a5fa" fontSize="10" fontWeight="600" letterSpacing="3">SEASONAL RIVER</text>

                {/* Green Belt */}
                <rect x="0" y={layout.rows.green.y} width={layout.VB_W} height={layout.rows.green.h} fill="#166534" rx="2" opacity="0.4" />
                <text x={layout.VB_W / 2} y={layout.rows.green.y + 16} textAnchor="middle" fill="#4ade80" fontSize="9" fontWeight="600" letterSpacing="2">GREEN BELT (10M)</text>

                {/* 15M Road */}
                <rect x="0" y={layout.rows.road15.y} width={layout.VB_W} height={layout.rows.road15.h} fill="#334155" rx="2" />
                <text x={layout.VB_W / 2} y={layout.rows.road15.y + 18} textAnchor="middle" fill="#94a3b8" fontSize="10" fontWeight="700" letterSpacing="3">15.00M WIDE ROAD</text>

                {/* 12M Road */}
                <rect x="0" y={layout.rows.road12.y} width={layout.VB_W} height={layout.rows.road12.h} fill="#334155" rx="2" />
                <text x={layout.VB_W / 2} y={layout.rows.road12.y + 16} textAnchor="middle" fill="#94a3b8" fontSize="9" fontWeight="700" letterSpacing="3">12.00M WIDE ROAD</text>

                {/* Southern boundary */}
                <rect x="0" y={layout.rows.south.y} width={layout.VB_W} height={layout.rows.south.h} fill="#1e293b" rx="2" opacity="0.4" />
                <text x={layout.VB_W / 2} y={layout.rows.south.y + 18} textAnchor="middle" fill="#475569" fontSize="9" fontWeight="600" letterSpacing="2">BOUNDARY</text>

                {/* Plot Rectangles */}
                {filteredPlots.map(plot => {
                  const r = layout.rects[plot.plot_id];
                  if (!r) return null;
                  const isHovered = hoveredPlot === plot.plot_id;
                  const isSelected = selectedPlot?.plot_id === plot.plot_id;
                  const sc = plot.corner_plot ? STATUS_COLORS.premium : STATUS_COLORS[plot.status] || STATUS_COLORS.available;
                  const opacity = isHovered || isSelected ? 0.9 : 0.65;

                  return (
                    <g key={plot.plot_id}
                      onClick={() => setSelectedPlot(plot)}
                      onMouseEnter={() => setHoveredPlot(plot.plot_id)}
                      onMouseLeave={() => setHoveredPlot(null)}
                      style={{ cursor: "pointer" }}
                      data-testid={`map-plot-${plot.plot_id}`}
                    >
                      <rect
                        x={r.x} y={r.y} width={r.w} height={r.h}
                        fill={sc.fill}
                        stroke={isSelected ? "#fff" : isHovered ? "#fff" : sc.stroke}
                        strokeWidth={isSelected ? 2.5 : isHovered ? 2 : 1}
                        opacity={opacity}
                        rx="2"
                      />
                      {/* Glow on hover */}
                      {(isHovered || isSelected) && (
                        <rect
                          x={r.x - 2} y={r.y - 2} width={r.w + 4} height={r.h + 4}
                          fill="none" stroke={sc.fill} strokeWidth="1" opacity="0.4" rx="3"
                        />
                      )}
                      {/* Plot ID */}
                      <text
                        x={r.x + r.w / 2} y={r.y + r.h / 2 - (r.h > 50 ? 6 : 0)}
                        textAnchor="middle" dominantBaseline="central"
                        fill="#fff" fontSize={r.w < 50 ? "8" : r.w < 80 ? "10" : "12"}
                        fontWeight="800" fontFamily="'Barlow Condensed', sans-serif"
                      >
                        {plot.plot_id}
                      </text>
                      {/* Size label (if enough space) */}
                      {r.h > 50 && r.w > 55 && (
                        <text
                          x={r.x + r.w / 2} y={r.y + r.h / 2 + 12}
                          textAnchor="middle" dominantBaseline="central"
                          fill="rgba(255,255,255,0.7)" fontSize="7"
                          fontFamily="'JetBrains Mono', monospace"
                        >
                          {plot.plot_size_sqmt.toLocaleString()} sqmt
                        </text>
                      )}
                      {/* Corner badge */}
                      {plot.corner_plot && r.w > 50 && (
                        <text
                          x={r.x + r.w - 3} y={r.y + 10}
                          textAnchor="end" fill="#fbbf24" fontSize="7" fontWeight="700"
                        >
                          CORNER
                        </text>
                      )}
                    </g>
                  );
                })}

                {/* Title & Compass */}
                <text x="20" y={layout.VB_H - 15} fill="#475569" fontSize="10" fontWeight="600" fontFamily="'JetBrains Mono', monospace" letterSpacing="1">
                  IIE SIGADDI | SIIDCUL KOTDWAR | PAURI GARHWAL | UTTARAKHAND
                </text>
                <g transform={`translate(${layout.VB_W - 50}, ${layout.VB_H - 50})`}>
                  <circle cx="0" cy="0" r="18" fill="none" stroke="#475569" strokeWidth="1" />
                  <text x="0" y="-8" textAnchor="middle" fill="#94a3b8" fontSize="8" fontWeight="700">N</text>
                  <line x1="0" y1="-15" x2="0" y2="-5" stroke="#f97316" strokeWidth="2" />
                  <line x1="0" y1="5" x2="0" y2="15" stroke="#475569" strokeWidth="1" />
                </g>
              </svg>
            </div>
          )}

          {/* Side Panel */}
          <AnimatePresence>
            {selectedPlot && (
              <PlotDetailPanel
                plot={selectedPlot}
                onClose={() => setSelectedPlot(null)}
                onReserve={(p) => setReservingPlot(p)}
              />
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Reservation Modal */}
      <AnimatePresence>
        {reservingPlot && (
          <ReservationForm
            plot={reservingPlot}
            onClose={() => setReservingPlot(null)}
            onSuccess={handleReservationSuccess}
          />
        )}
      </AnimatePresence>

      {/* Hovered Plot Tooltip */}
      {hoveredPlot && !selectedPlot && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 bg-slate-900 border border-white/10 px-4 py-2 flex items-center gap-3 shadow-lg"
          data-testid="plot-tooltip">
          {(() => {
            const p = plots.find(pp => pp.plot_id === hoveredPlot);
            if (!p) return null;
            return (
              <>
                <span className="text-white font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{p.plot_id}</span>
                <span className="text-slate-400 text-xs">{p.plot_size_sqmt.toLocaleString()} sqmt</span>
                <span className="text-orange-400 text-xs font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {`\u20B9${p.price_per_sqmt?.toLocaleString("en-IN")}/sqmt`}
                </span>
                <span className={`text-xs font-bold px-1.5 py-0.5 ${p.status === "available" ? "bg-emerald-500/20 text-emerald-300" : p.status === "reserved" ? "bg-yellow-500/20 text-yellow-300" : "bg-red-500/20 text-red-300"}`}>
                  {p.status.toUpperCase()}
                </span>
                {p.corner_plot && <span className="bg-blue-500/20 text-blue-300 text-xs font-bold px-1.5 py-0.5">CORNER</span>}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
